import { useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type { PagaebleResult, SearchTareaDTO, SearchVendedorDTO, TareaPrioridad, TareaTipo, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import {
    dueView,
    editTaskPath,
    enumLabel,
    errorMessage,
    isClosed,
    NEW_TASK_PATH,
    pageWindow,
    personName,
    TASKS_PATH,
} from '../common/task-common'
import type { DueView, FormMessage, TasksLocationState } from '../common/task-common'

export const PAGE_SIZES = [20, 50, 100]
const TEXT_DELAY_MS = 400
const LOAD_ERROR = 'No se pudo cargar el listado de tareas.'

export interface TaskListFilters {
    nombre: string
    descripcion: string
    vendedor: SearchVendedorDTO | null
    verCerradas: boolean
}

export type TaskTextFilter = 'nombre' | 'descripcion'

export interface TaskRow {
    id: number
    nombre: string
    descripcion: string
    closed: boolean
    tipo: TareaTipo
    tipoLabel: string
    prioridad: TareaPrioridad | null
    prioridadLabel: string
    seller: string
    due: DueView
}

const defaultFilters: TaskListFilters = {
    nombre: '',
    descripcion: '',
    vendedor: null,
    verCerradas: false,
}

export const NEW_TASK_LOCATION: { pathname: string; state: TasksLocationState } = {
    pathname: NEW_TASK_PATH,
    state: { backUrl: TASKS_PATH },
}

const buildQuery = (filters: TaskListFilters) => {
    const params: string[] = []
    if (filters.nombre.trim()) params.push(`nombre[cn]=${encodeURIComponent(filters.nombre.trim())}`)
    if (filters.descripcion.trim()) params.push(`descripcion[cn]=${encodeURIComponent(filters.descripcion.trim())}`)
    if (filters.vendedor?.id) params.push(`vendedor_id=${filters.vendedor.id}`)
    if (!filters.verCerradas) params.push('status=abierta')
    return params.join('&')
}

const toRow = (item: SearchTareaDTO): TaskRow => ({
    id: item.id,
    nombre: item.nombre || '',
    descripcion: item.descripcion || '',
    closed: isClosed(item.status),
    tipo: item.tipo?.name || 'tarea',
    tipoLabel: enumLabel(item.tipo),
    prioridad: item.prioridad?.name || null,
    prioridadLabel: enumLabel(item.prioridad),
    seller: personName({ nombres: item.vendedorNombres, apPat: item.vendedorApPat, apMat: item.vendedorApMat }),
    due: dueView(item.vencimiento, item.status),
})

export const useTaskListModel = () => {
    const history = useHistory()
    const location = useLocation()
    const { tareaId } = useParams()
    const locationState = location.state as TasksLocationState | null

    const filtersRef = useRef<TaskListFilters>(defaultFilters)
    const textTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const [filters, setFilters] = useState<TaskListFilters>(defaultFilters)
    const [query, setQuery] = useState(() => buildQuery(defaultFilters))
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [message, setMessage] = useState<FormMessage | null>(locationState?.flash ? { type: 'success', message: locationState.flash } : null)
    const [items, setItems] = useState<SearchTareaDTO[]>([])
    const [totalItems, setTotalItems] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [detailId, setDetailId] = useState<number | null>(tareaId ? Number(tareaId) || null : null)

    useEffect(() => {
        if (locationState?.flash) history.replace(TASKS_PATH)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => () => clearTimeout(textTimer.current), [])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const extra = query ? `&${query}` : ''
                const response = await APIInvoke.invokeGET(`/tareas?page=${page}&size=${pageSize}&order=vencimiento asc${extra}`)
                const payload: WrapperResponse<PagaebleResult<SearchTareaDTO>> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setItems(payload.body.items || [])
                setTotalItems(payload.body.totalItems || 0)
                setTotalPages(payload.body.totalPages || 0)
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [page, pageSize, query, reloadToken])

    const commit = (next: TaskListFilters) => {
        clearTimeout(textTimer.current)
        filtersRef.current = next
        setFilters(next)
        setPage(1)
        setQuery(buildQuery(next))
    }

    const commitPatch = (partial: Partial<TaskListFilters>) => commit({ ...filtersRef.current, ...partial })

    const changeText = (key: TaskTextFilter, value: string) => {
        const next = { ...filtersRef.current, [key]: value }
        filtersRef.current = next
        setFilters(next)
        clearTimeout(textTimer.current)
        textTimer.current = setTimeout(() => {
            setPage(1)
            setQuery(buildQuery(filtersRef.current))
        }, TEXT_DELAY_MS)
    }

    const changePageSize = (value: number) => {
        setPageSize(Number(value))
        setPage(1)
    }

    const closeTarea = () => {
        setDetailId(null)
        if (tareaId) history.replace(TASKS_PATH)
    }

    const rangeFrom = totalItems === 0 ? 0 : ((page - 1) * pageSize) + 1

    return {
        filters,
        page,
        pageSize,
        loading,
        initialLoadDone,
        error,
        message,
        rows: items.map(toRow),
        totalItems,
        totalPages,
        pages: totalPages > 0 ? pageWindow(page, totalPages) : [],
        rangeFrom,
        rangeTo: totalItems === 0 ? 0 : rangeFrom + items.length - 1,
        detailId,
        changeText,
        submitText: () => commit(filtersRef.current),
        changeVendedor: (vendedor: SearchVendedorDTO | null) => commitPatch({ vendedor }),
        changeVerCerradas: (verCerradas: boolean) => commitPatch({ verCerradas }),
        clearFilters: () => commit({ ...defaultFilters }),
        setPage,
        changePageSize,
        reload: () => setReloadToken((token) => token + 1),
        dismissMessage: () => setMessage(null),
        openTarea: (id: number) => setDetailId(id),
        closeTarea,
        editTarea: (id: number) => history.push({ pathname: editTaskPath(id), state: { backUrl: TASKS_PATH } satisfies TasksLocationState }),
    }
}
